// src/components/meeting/RoomScreen.js

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSocket } from '../../hooks/SocketContext';
import Peer from 'simple-peer';
import { useNavigate } from 'react-router-dom';
import hark from 'hark';
import { setCookie, getCookie } from '../../utils/cookieUtils';
import AudioVisualizer from './AudioVisualizer';

// --- Corrected Video Component ---
const Video = ({ peer }) => {
    const ref = useRef();
    const [stream, setStream] = useState(null);

    useEffect(() => {
        if (!peer) return;
        const handleStream = (mediaStream) => {
            setStream(mediaStream);
            if (ref.current) ref.current.srcObject = mediaStream;
        };
        const handleError = (err) => console.error('Peer error:', err);
        peer.on('stream', handleStream);
        peer.on('error', handleError);
        return () => {
            peer.removeListener('stream', handleStream);
            peer.removeListener('error', handleError);
        };
    }, [peer]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: '#2C2F33' }}>
            <video playsInline autoPlay ref={ref} style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
            {stream && <AudioVisualizer stream={stream} />}
        </div>
    );
};


// --- UI Components ---
const ControlButton = ({ onClick, title, disabled = false, children, active = false, danger = false }) => (
    <button onClick={onClick} disabled={disabled} aria-label={title} title={title} style={{ background: danger ? '#d83c3e' : (active ? '#43b581' : '#3a3d41'), border: 'none', borderRadius: '50%', width: '55px', height: '55px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer', color: 'white', margin: '0 10px', transition: 'background-color 0.2s, opacity 0.2s', opacity: disabled ? 0.5 : 1, }}>
        {children}
    </button>
);

const ParticipantsPanel = ({ participants, onClose, isOpen }) => (
    <div style={{ position: 'fixed', top: 0, right: isOpen ? '0' : '-320px', width: '300px', height: '100%', background: '#2f3136', zIndex: 50, display: 'flex', flexDirection: 'column', padding: '20px', boxShadow: '-5px 0 15px rgba(0,0,0,0.2)', transition: 'right 0.3s ease-in-out' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Participants ({participants.length})</h2>
            <button onClick={onClose} aria-label="Close participants panel" style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, overflowY: 'auto', flex: 1 }}>
            {participants.map((p, index) => (
                <li key={index} style={{ padding: '10px', borderBottom: '1px solid #40444b' }}>{p.userName}</li>
            ))}
        </ul>
    </div>
);

// --- NEW: Fully Accessible & Stable Settings Modal ---
const SettingsModal = ({ isOpen, onClose, onApply }) => {
    const [devices, setDevices] = useState({ audio: [], video: [] });
    const [selectedAudio, setSelectedAudio] = useState(getCookie('preferredAudio') || '');
    const [selectedVideo, setSelectedVideo] = useState(getCookie('preferredVideo') || '');
    const [selectedQuality, setSelectedQuality] = useState(getCookie('preferredQuality') || '720p');
    const modalRef = useRef(null);

    const getDevices = useCallback(async () => {
        try {
            const deviceInfos = await navigator.mediaDevices.enumerateDevices();
            const audioDevices = deviceInfos.filter(d => d.kind === 'audioinput');
            const videoDevices = deviceInfos.filter(d => d.kind === 'videoinput');
            setDevices({ audio: audioDevices, video: videoDevices });

            if ((!selectedAudio && audioDevices.length > 0) || !audioDevices.some(d => d.deviceId === selectedAudio)) {
                setSelectedAudio(audioDevices[0]?.deviceId || '');
            }
            if ((!selectedVideo && videoDevices.length > 0) || !videoDevices.some(d => d.deviceId === selectedVideo)) {
                setSelectedVideo(videoDevices[0]?.deviceId || '');
            }
        } catch (error) { console.error("Could not get devices:", error); }
    }, [selectedAudio, selectedVideo]);

    useEffect(() => {
        if (!isOpen) return;

        document.body.style.overflow = 'hidden';
        getDevices();
        
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Tab') {
                const focusableElements = modalRef.current?.querySelectorAll('select, button');
                if (!focusableElements) return;
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];
                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        lastElement.focus();
                        e.preventDefault();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        firstElement.focus();
                        e.preventDefault();
                    }
                }
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        modalRef.current?.focus();

        return () => {
            document.body.style.overflow = 'auto';
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, getDevices]);

    const handleApply = () => onApply({ audioId: selectedAudio, videoId: selectedVideo, quality: selectedQuality });

    if (!isOpen) return null;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
            <div ref={modalRef} tabIndex="-1" style={{ outline: 'none', background: '#2f3136', padding: '25px', borderRadius: '8px', width: '90%', maxWidth: '500px', boxShadow: '0 5px 20px rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true" aria-labelledby="settings-title" onClick={e => e.stopPropagation()}>
                <h2 id="settings-title" style={{ marginTop: 0, marginBottom: '20px' }}>Settings</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                        <label htmlFor="video-select-modal">Camera:</label>
                        <select id="video-select-modal" value={selectedVideo} onChange={e => setSelectedVideo(e.target.value)} style={{ width: '100%', padding: '8px', background: '#40444b', color: 'white', border: '1px solid #555', borderRadius: '4px' }}>
                            {devices.video.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="audio-select-modal">Microphone:</label>
                        <select id="audio-select-modal" value={selectedAudio} onChange={e => setSelectedAudio(e.target.value)} style={{ width: '100%', padding: '8px', background: '#40444b', color: 'white', border: '1px solid #555', borderRadius: '4px' }}>
                            {devices.audio.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="quality-select-modal">Video Quality:</label>
                        <select id="quality-select-modal" value={selectedQuality} onChange={e => setSelectedQuality(e.target.value)} style={{ width: '100%', padding: '8px', background: '#40444b', color: 'white', border: '1px solid #555', borderRadius: '4px' }}>
                            <option value="144p">144p (Lowest)</option>
                            <option value="360p">360p (Standard)</option>
                            <option value="720p">720p (HD)</option>
                            <option value="1080p">1080p (Full HD)</option>
                        </select>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '25px' }}>
                    <button onClick={getDevices} style={{ padding: '10px 20px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer' }}>Refresh Devices</button>
                    <div>
                        <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: '4px', background: '#555', color: 'white', border: 'none', cursor: 'pointer', marginRight: '10px' }}>Cancel</button>
                        <button onClick={handleApply} style={{ padding: '10px 20px', borderRadius: '4px', background: '#43b581', color: 'white', border: 'none', cursor: 'pointer' }}>Apply</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- NEW: Simple Alert Component ---
const AlertBar = ({ message, type, onDismiss }) => {
    useEffect(() => {
        if (message) {
            const timer = setTimeout(onDismiss, 5000); // Auto-dismiss after 5 seconds
            return () => clearTimeout(timer);
        }
    }, [message, onDismiss]);

    if (!message) return null;

    const styles = {
        info: { backgroundColor: '#3b82f6' },
        success: { backgroundColor: '#16a34a' },
        error: { backgroundColor: '#ef4444' },
    };

    return (
        <div role="alert" style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', padding: '10px 20px', borderRadius: '8px', color: 'white', zIndex: 1000, ...styles[type] }}>
            {message}
        </div>
    );
};


const qualityLevels = {
    '144p': { width: { ideal: 256 }, height: { ideal: 144 }, frameRate: { ideal: 15 } },
    '360p': { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 30 } },
    '720p': { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
    '1080p': { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
};

// --- Main RoomScreen Component ---
const RoomScreen = ({ meeting, user, guestName, localStream: initialLocalStream, initialQuality }) => {
    const socket = useSocket();
    const navigate = useNavigate();
    
    const [peers, setPeers] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [isMicOn, setIsMicOn] = useState(() => initialLocalStream.getAudioTracks()[0]?.enabled);
    const [isCamOn, setIsCamOn] = useState(() => initialLocalStream.getVideoTracks()[0]?.enabled);
    const [activeSpeaker, setActiveSpeaker] = useState(null);
    const [quality, setQuality] = useState(initialQuality);
    
    const [showParticipants, setShowParticipants] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [alert, setAlert] = useState({ message: '', type: 'info' });

    const peersRef = useRef([]);
    const userVideo = useRef();
    const localStreamRef = useRef(initialLocalStream);
    const displayName = (user && user.first_name) ? `${user.first_name} ${user.last_name}` : (guestName || 'Guest');

    const showAlert = (message, type = 'info') => setAlert({ message, type });

    useEffect(() => {
        if (userVideo.current) userVideo.current.srcObject = localStreamRef.current;
    }, []);

    useEffect(() => {
        if (!socket || !localStreamRef.current || !meeting?.id) return;
        const roomId = meeting.id;

        const speechEvents = hark(localStreamRef.current, {});
        speechEvents.on('speaking', () => setActiveSpeaker(socket.id));
        speechEvents.on('stopped_speaking', () => setActiveSpeaker(prev => prev === socket.id ? null : prev));

        const handleUserJoined = (payload) => {
            showAlert(`${payload.userName} has joined the meeting.`, 'info');
            const peer = addPeer(payload.signal, payload.callerID, localStreamRef.current);
            const peerInfo = { userName: payload.userName, socketId: payload.callerID };
            peersRef.current.push({ peerID: payload.callerID, peer, info: peerInfo });
            setPeers(current => [...current, { peerID: payload.callerID, peer, info: peerInfo }]);
            setParticipants(prev => [...prev, peerInfo]);
        };
        const handleAllUsers = (users) => {
            const createdPeers = users.map(peerInfo => {
                const peer = createPeer(peerInfo.socketId, socket.id, localStreamRef.current);
                peersRef.current.push({ peerID: peerInfo.socketId, peer, info: peerInfo });
                return { peerID: peerInfo.socketId, peer, info: peerInfo };
            });
            setPeers(createdPeers);
            setParticipants(prev => [...prev, ...users]);
        };
        const handleSignalReturned = (payload) => peersRef.current.find(p => p.peerID === payload.id)?.peer.signal(payload.signal);
        const handleUserLeft = (id) => {
            const peerObj = peersRef.current.find(p => p.peerID === id);
            if (peerObj) showAlert(`${peerObj.info.userName} has left the meeting.`, 'info');
            peerObj?.peer.destroy();
            peersRef.current = peersRef.current.filter(p => p.peerID !== id);
            setPeers(current => current.filter(p => p.peerID !== id));
            setParticipants(prev => prev.filter(p => p.socketId !== id));
        };
        const handleUserSpeaking = ({ socketId }) => setActiveSpeaker(socketId);
        const handleUserStoppedSpeaking = ({ socketId }) => setActiveSpeaker(prev => prev === socketId ? null : prev);

        socket.on("all-users", handleAllUsers);
        socket.on("user-joined", handleUserJoined);
        socket.on("receiving-returned-signal", handleSignalReturned);
        socket.on("user-left", handleUserLeft);
        socket.on('user-speaking', handleUserSpeaking);
        socket.on('user-stopped-speaking', handleUserStoppedSpeaking);
        
        socket.emit("join-room", { roomId, userId: socket.id, userName: displayName });
        setParticipants([{ socketId: socket.id, userName: `${displayName} (You)` }]);

        return () => {
            socket.off("all-users", handleAllUsers);
            socket.off("user-joined", handleUserJoined);
            socket.off("receiving-returned-signal", handleSignalReturned);
            socket.off("user-left", handleUserLeft);
            socket.off('user-speaking', handleUserSpeaking);
            socket.off('user-stopped-speaking', handleUserStoppedSpeaking);
        };
    }, [socket, meeting, user, displayName]);

    const createPeer = (userToSignal, callerID, stream) => {
        const peer = new Peer({ initiator: true, trickle: false, stream });
        peer.on("signal", signal => socket.emit("sending-signal", { userToSignal, callerID, signal }));
        return peer;
    };
    const addPeer = (incomingSignal, callerID, stream) => {
        const peer = new Peer({ initiator: false, trickle: false, stream });
        peer.on("signal", signal => socket.emit("returning-signal", { signal, callerID }));
        peer.signal(incomingSignal);
        return peer;
    };

    const handleToggleMic = () => {
        const enabled = !isMicOn;
        localStreamRef.current.getAudioTracks()[0].enabled = enabled;
        setIsMicOn(enabled);
    };
    const handleToggleCam = () => {
        const enabled = !isCamOn;
        localStreamRef.current.getVideoTracks()[0].enabled = enabled;
        setIsCamOn(enabled);
    };
    
    const handleApplySettings = async ({ audioId, videoId, quality: newQuality }) => {
        setShowSettings(false);
        setQuality(newQuality);
        setCookie('preferredQuality', newQuality, 365);
        setCookie('preferredAudio', audioId, 365);
        setCookie('preferredVideo', videoId, 365);

        try {
            const constraints = {
                audio: { deviceId: { exact: audioId } },
                video: { deviceId: { exact: videoId }, ...qualityLevels[newQuality] }
            };
            const newStream = await navigator.mediaDevices.getUserMedia(constraints);
            
            if (userVideo.current) userVideo.current.srcObject = newStream;
            
            const oldStream = localStreamRef.current;
            localStreamRef.current = newStream;

            const newAudioTrack = newStream.getAudioTracks()[0];
            const newVideoTrack = newStream.getVideoTracks()[0];
            for (const peerInfo of peersRef.current) {
                const peer = peerInfo.peer;
                const oldAudioTrack = peer.streams[0]?.getAudioTracks()[0];
                const oldVideoTrack = peer.streams[0]?.getVideoTracks()[0];
                if (oldAudioTrack) peer.replaceTrack(oldAudioTrack, newAudioTrack, newStream);
                if (oldVideoTrack) peer.replaceTrack(oldVideoTrack, newVideoTrack, newStream);
            }
            
            oldStream.getTracks().forEach(track => track.stop());
            showAlert('Settings applied successfully.', 'success');

        } catch (error) {
            console.error("Failed to apply new settings:", error);
            showAlert('Could not apply new settings. Please ensure the devices are available.', 'error');
        }
    };

    const handleLeaveRoom = useCallback(() => {
        if (socket) socket.disconnect();
        localStreamRef.current.getTracks().forEach(track => track.stop());
        navigate('/');
    }, [socket, navigate]);

    return (
        <div style={{ display: 'flex', height: '100vh', flexDirection: 'column', backgroundColor: '#202225', color: 'white', position: 'relative', overflow: 'hidden' }}>
            <AlertBar message={alert.message} type={alert.type} onDismiss={() => setAlert({ message: '', type: 'info' })} />
            <header style={{ padding: '10px 20px', backgroundColor: '#2f3136', zIndex: 10 }}>
                <h1 style={{ margin: 0 }}>
                  {meeting?.name && meeting?.description
                    ? `${meeting.name} - ${meeting.description}`
                    : 'Meeting'}
                </h1>

            </header>

            <main style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', padding: '20px', overflowY: 'auto' }}>
                <div style={{ border: activeSpeaker === socket?.id ? '4px solid #43B581' : '4px solid transparent', borderRadius: '12px', overflow: 'hidden', position: 'relative', backgroundColor: '#2C2F33' }}>
                    <video style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)', display: isCamOn ? 'block' : 'none' }} muted ref={userVideo} autoPlay playsInline />
                    {!isCamOn && <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Camera Off</div>}
                    <div style={{ position: 'absolute', bottom: '10px', left: '10px', backgroundColor: 'rgba(0,0,0,0.6)', padding: '5px 10px', borderRadius: '5px' }}>
                        {displayName} (You)
                    </div>
                </div>
                {peers.map(p => (
                    <div key={p.peerID} style={{ border: activeSpeaker === p.peerID ? '4px solid #43B581' : '4px solid transparent', borderRadius: '12px', overflow: 'hidden', position: 'relative', backgroundColor: '#2C2F33' }}>
                        <Video peer={p.peer} />
                        <div style={{ position: 'absolute', bottom: '10px', left: '10px', backgroundColor: 'rgba(0,0,0,0.6)', padding: '5px 10px', borderRadius: '5px' }}>
                           {p.info?.userName || 'Guest'}
                        </div>
                    </div>
                ))}
            </main>
            
            <ParticipantsPanel participants={participants} isOpen={showParticipants} onClose={() => setShowParticipants(false)} />
            <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} onApply={handleApplySettings} />

            <footer style={{ padding: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#2f3136', zIndex: 10 }}>
                <ControlButton onClick={handleToggleMic} title={isMicOn ? "Mute" : "Unmute"} active={!isMicOn}>Mic</ControlButton>
                <ControlButton onClick={handleToggleCam} title={isCamOn ? "Stop Camera" : "Start Camera"} active={!isCamOn}>Cam</ControlButton>
                <ControlButton onClick={() => {setShowParticipants(p => !p); setShowSettings(false);}} title="Participants" active={showParticipants}>Users</ControlButton>
                <ControlButton onClick={() => {setShowSettings(s => !s); setShowParticipants(false);}} title="Settings" active={showSettings}>Settings</ControlButton>
                <ControlButton onClick={handleLeaveRoom} title="Leave Call" danger>Leave</ControlButton>
            </footer>
        </div>
    );
};

export default RoomScreen;
